import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { AlertTriangle } from "lucide-react";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CatalogueMigration } from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { MIGRATION_STEP_LABELS } from "@shared-core/domain/catalogue-migration";
import { formatCount, formatDateOrDash } from "@shared-core/domain/formatters";
import { CUTOVER_STATE_LABELS } from "@shared-core/domain/stock-replenishment";
import { paths } from "@shared-core/routing/routes";
import { SettingsBreadcrumbs } from "../app/components/patterns/SettingsBreadcrumbs";
import { Badge } from "../app/components/primitives/badge";
import { ReadinessTab } from "./catalogue-migration/ReadinessTab";
import { ReviewListTab } from "./catalogue-migration/ReviewListTab";
import { ShadowTab } from "./catalogue-migration/ShadowTab";
import {
  CatalogueMigrationEnvironmentBadge,
  isProductionEnvironment,
} from "./catalogue-migration/environment";

type Tab = "review" | "readiness" | "shadow";

/**
 * One customer's migration: the review list (what becomes of each piece of
 * its repeat work and held stock), the readiness checklist with the "Cut
 * over…" action, and, on staging only, the shadow against Tharstern's copy.
 */
export function SettingsCatalogueMigrationCustomerPage({
  environment = getRuntimeConfig().environment,
}: {
  environment?: string;
}) {
  const { customerId = "" } = useParams<{ customerId: string }>();
  const dataAdapter = useDataAdapter();
  const isProduction = isProductionEnvironment(environment);
  const [tab, setTab] = useState<Tab>("review");
  const [migration, setMigration] = useState<CatalogueMigration | null>(null);
  const [status, setStatus] = useState<
    "loading" | "ready" | "not_found" | "error"
  >("loading");
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setMigration(await dataAdapter.getCatalogueMigration(customerId));
      setStatus("ready");
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        setStatus("not_found");
        return;
      }
      console.error("catalogue_migration.customer.load_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setStatus("error");
    }
  }, [dataAdapter, customerId]);

  useEffect(() => {
    setStatus("loading");
    void load();
  }, [load]);

  const tabs: { id: Tab; label: string }[] = [
    { id: "review", label: "Review list" },
    { id: "readiness", label: "Readiness" },
    ...(isProduction ? [] : [{ id: "shadow" as Tab, label: "Shadow" }]),
  ];
  const name = migration?.customer_name ?? customerId;

  return (
    <div className="space-y-6">
      <SettingsBreadcrumbs
        crumbs={[
          { label: "Settings", to: paths.settingsHome() },
          {
            label: "Catalogue migration",
            to: paths.settingsCatalogueMigration(),
          },
          { label: name },
        ]}
      />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl">{name}</h1>
            {migration && (
              <Badge
                variant={migration.step === "cut_over" ? "success" : "neutral"}
              >
                {MIGRATION_STEP_LABELS[migration.step]}
              </Badge>
            )}
            {migration?.cutover_state === "shadow" && (
              <Badge variant="warning">{CUTOVER_STATE_LABELS.shadow}</Badge>
            )}
          </div>
          {migration && (
            <p className="text-sm text-muted-foreground">
              {[
                migration.customer_number,
                migration.tharstern_customer_code &&
                  `Tharstern ${migration.tharstern_customer_code}`,
                migration.has_migration
                  ? migration.reviewed_by
                    ? `migration file reviewed ${formatDateOrDash(migration.reviewed_on)} by ${migration.reviewed_by}`
                    : "migration file not reviewed yet"
                  : "no migration file yet",
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </div>
        <CatalogueMigrationEnvironmentBadge environment={environment} />
      </div>

      {notice && (
        <div
          role="status"
          className="text-sm p-3 rounded-lg border border-green-300 bg-green-50 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200"
        >
          {notice}
        </div>
      )}

      {status === "loading" ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-muted-foreground">Loading…</div>
        </div>
      ) : status === "not_found" ? (
        <div className="text-sm p-6 rounded-lg border border-border text-muted-foreground">
          No such customer.{" "}
          <Link to={paths.settingsCatalogueMigration()} className="underline">
            Back to the customers
          </Link>
          .
        </div>
      ) : status === "error" || migration === null ? (
        <div
          role="alert"
          className="flex items-center gap-2 text-sm p-4 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="size-4 shrink-0" />
          Couldn't load this customer's migration. Please try again.
        </div>
      ) : (
        <>
          {migration.cutover_state === "cut_over" && (
            <div
              role="status"
              className="text-sm p-3 rounded-lg border border-green-300 bg-green-50 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200"
            >
              Cut over on {formatDateOrDash(migration.cut_over_at)}
              {migration.cut_over_by_name &&
                ` by ${migration.cut_over_by_name}`}
              : {formatCount(migration.held_stock_count)} stock items now in One
              {migration.unlinked_count > 0 &&
                `, ${formatCount(migration.unlinked_count)} of them unlinked`}
              .
            </div>
          )}
          <div
            role="tablist"
            aria-label="Catalogue migration"
            className="flex flex-wrap gap-1 border-b border-border pb-2"
          >
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`migration-tab-${t.id}`}
                aria-selected={tab === t.id}
                aria-controls={`migration-panel-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1 rounded-md text-sm transition-colors ${
                  tab === t.id
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div
            role="tabpanel"
            id={`migration-panel-${tab}`}
            aria-labelledby={`migration-tab-${tab}`}
          >
            {tab === "review" ? (
              <ReviewListTab migration={migration} />
            ) : tab === "readiness" ? (
              <ReadinessTab
                migration={migration}
                environment={environment}
                onCutOver={(message) => {
                  setNotice(message);
                  void load();
                }}
                onArtworkSearchStarted={setNotice}
              />
            ) : (
              <ShadowTab
                migration={migration}
                onStarted={(message) => {
                  setNotice(message);
                  void load();
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
